import React, { useEffect, useMemo, useState } from "react";
import { Box, Grid } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { connect, useDispatch, useSelector } from "react-redux";
import { Actions } from "../../../../core/modules/Actions";
import HeadingComponent from "../../../../component/atom/Headings/Heading";
import { TextIconButtonComponent } from "../../../../component/atom";
import { useQuizForm } from "../../../../hook/useQuizForm";
import QuizFormBody from "../../../../component/molecule/Cards/admin/QuizFormBody";
import GuestRepository from "../../../../core/repository/QuizeRepository";
import { useQuizFolders } from "../../../../hook/useQuizFolders";

const AdminQuizFirstScreen = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const activeFolderId =
    location.state?.folderId ??
    JSON.parse(sessionStorage.getItem("quiz_active_folder_id") || "null");

  const activeCourseId =
    location.state?.courseId ??
    JSON.parse(sessionStorage.getItem("quiz_active_course_id") || "null");

  const activeBreadcrumbTrail =
    location.state?.breadcrumbTrail ??
    JSON.parse(sessionStorage.getItem("quiz_breadcrumb_trail") || "null");

  const hasActiveFolder = activeFolderId !== null && activeFolderId !== undefined;

  const { folders, loadingFolders } = useQuizFolders();
  const folderCourseId = useMemo(() => {
    if (!hasActiveFolder) return null;

    const directoryData = folders?.quiz_directories || folders?.data?.quiz_directories || {};
    const findCourseId = (directoryList) => {
      for (const directory of Object.values(directoryList || {})) {
        const directoryCourseId =
          directory?.course_id ?? directory?.courseId ?? directory?.course?.id;
        if (String(directory?.id) === String(activeFolderId)) return directoryCourseId;
        const childCourseId = findCourseId(directory?.children);
        if (childCourseId != null) return childCourseId;
      }
      return null;
    };

    return findCourseId(directoryData) ?? activeCourseId;
  }, [activeCourseId, activeFolderId, folders, hasActiveFolder]);
  const form = useQuizForm(
    null,
    hasActiveFolder ? activeFolderId : null,
    hasActiveFolder ? folderCourseId : null
  );
  const commonCourseList = useSelector(state => state.course?.get("allStatusCourseList") || state.students?.get("commonCourseList"));
  const [nameApiError, setNameApiError] = useState("");
  const [thumbnailApiError, setThumbnailApiError] = useState("");

  const setApiValidationError = (message) => {
    if (/thumbnail/i.test(message)) {
      setThumbnailApiError(message);
      return;
    }
    setNameApiError(message);
  };

  useEffect(() => {
    dispatch(Actions.course.getAllStateCourseList());
  }, [dispatch]);

  const handleSave = async () => {
    setNameApiError("");
    setThumbnailApiError("");

    if (form.validateForm()) {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("description", form.description);
      fd.append("duration", form.time);
      fd.append("no_of_attempts", form.noAttempt);
      fd.append("week", "Week");

      if (form.thumbnail) {
        const fileToUpload = form.thumbnail instanceof File
          ? form.thumbnail
          : form.thumbnail.file || form.thumbnail.raw;

        if (fileToUpload instanceof File) {
          fd.append("thumbnail", fileToUpload);
        }
      }

      let coursesToAppend = [];
      if (Array.isArray(form.selectedCourses) && form.selectedCourses.length > 0) {
        coursesToAppend = form.selectedCourses;
      } else if (form.selectedCourse) {
        coursesToAppend = [form.selectedCourse];
      }

      const validCourseIds = coursesToAppend.filter(Boolean);
      validCourseIds.forEach((id, index) => {
        fd.append(`course_ids[${index}]`, Number(id));
      });

      // Directory IDs
      if (Array.isArray(form.selectedFolderIds) && form.selectedFolderIds.length > 0) {
        const validFolderIds = form.selectedFolderIds.filter(Boolean);
        validFolderIds.forEach((id, index) => {
          fd.append(`directory_ids[${index}]`, id);
        });
      }

      try {
        const res = await GuestRepository.onCreateQuiz(fd);
        const quizResult = res?.data;

        if (quizResult?.id) {
          const enrichedQuizData = {
            ...quizResult,
            course_ids: validCourseIds.map((id) => Number(id)),
          };

          localStorage.setItem("newQuiz", JSON.stringify(enrichedQuizData));

          navigate("/admin-quiz-second", {
            state: {
              id: quizResult.id,
              quizData: enrichedQuizData,
              folderId: activeFolderId,
              courseId: activeCourseId,
              breadcrumbTrail: activeBreadcrumbTrail,
              showSuccessDialog: true,
            },
          });
        } else {
          const apiMessage = res?.data?.message || res?.message;
          if (apiMessage) {
            setApiValidationError(apiMessage);
          }
        }
      } catch (error) {
        console.error("Error creating quiz:", error);
        const serverError =
          error?.response?.data?.message || "Something went wrong. Please try again.";
        setApiValidationError(serverError);
      }
    }
  };

  return (
    <Box className="main-screen-container">
      <Grid container alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Grid item>
          <HeadingComponent
            text={"Add Quiz"}
            fontweigth={600}
            size={40}
            backNavigation={true}
            fontfamily={"Montserrat"}
          />
        </Grid>

        <Grid item>
          <Box display="flex" gap={2}>
            <button type="button" className="cancel-btn" style={{ padding: "10px 24px", border: "1px solid #D9DCE3", borderRadius: 8, background: "#fff", cursor: "pointer" }} onClick={() => navigate(-1)}>Cancel</button>
            <TextIconButtonComponent btnText="Save & Next" onclick={handleSave} />
          </Box>
        </Grid>
      </Grid>

      <QuizFormBody
        form={form}
        folderItems={folders}
        loadingFolders={loadingFolders}
        courseList={commonCourseList}
        nameApiError={nameApiError}
        setNameApiError={setNameApiError}
        thumbnailApiError={thumbnailApiError}
      />

      <Grid mt={5} container justifyContent="flex-end">
        <Box display="flex" gap={2}>
          <button type="button" style={{ padding: "10px 24px", border: "1px solid #D9DCE3", borderRadius: 8, background: "#fff", cursor: "pointer" }} onClick={() => navigate(-1)}>Cancel</button>
          <TextIconButtonComponent
            btnText="Save & Next"
            onclick={handleSave}
          />
        </Box>
      </Grid>
    </Box>
  );
};

export default connect()(AdminQuizFirstScreen);